import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { rescheduleAppointmentThunk } from "../../../redux/Thunk/Appointment/AppointmentThunks";
import { getTodayString } from "../../../utils/helpers";

const CalendarIcon = () => (
  <svg className="w-5 h-5 text-primary-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5 text-gray-500 hover:text-gray-900 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = ({ className = "" }) => (
  <svg className={`w-4 h-4 text-gray-500 shrink-0 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-3.5 h-3.5 text-primary-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const TIME_SLOTS = [
  "08:00", "08:15", "08:30", "08:45",
  "09:00", "09:15", "09:30", "09:45",
  "10:00", "10:15", "10:30", "10:45",
  "11:00", "11:15", "11:30", "11:45",
  "12:00", "12:15", "12:30", "12:45",
  "13:00", "13:30", "14:00", "14:30",
  "17:00", "17:30", "18:00", "18:30",
  "19:00", "19:30", "20:00", "20:30"
];

const RescheduleModal = ({ appointment, onClose }) => {
  const dispatch = useDispatch();
  const selectedDate = useSelector((state) => state.appointment.selectedDate);

  const [newDate, setNewDate] = useState(
    appointment?.appointmentDate ? String(appointment.appointmentDate).split("T")[0] : getTodayString()
  );
  const [newTime, setNewTime] = useState(appointment?.slotStartTime || "10:00");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState(false);

  if (!appointment) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newDate || !newTime) return;

    setIsSubmitting(true);
    try {
      // Auto-calculate end time (15 mins later)
      const [h, m] = newTime.split(":").map(Number);
      const dt = new Date(2000, 0, 1, h, m + 15);
      const endH = String(dt.getHours()).padStart(2, "0");
      const endM = String(dt.getMinutes()).padStart(2, "0");
      const slotEndTime = `${endH}:${endM}`;

      await dispatch(
        rescheduleAppointmentThunk({
          AID: appointment.AID,
          appointmentDate: newDate,
          slotStartTime: newTime,
          slotEndTime,
          selectedDate,
        })
      ).unwrap();

      onClose();
    } catch (err) {
      console.error("Reschedule failed:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-xs animate-fadeIn">
      <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl w-full max-w-md overflow-hidden space-y-5 p-6 transform transition-all">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-primary-50 rounded-2xl border border-primary-100">
              <CalendarIcon />
            </div>
            <div>
              <h3 className="font-extrabold text-gray-900 text-base">
                Reschedule Appointment
              </h3>
              <p className="text-xs text-gray-500 font-medium">
                Change appointment date or time slot
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-all cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Patient Summary Card */}
        <div className="bg-gray-50/80 p-3.5 rounded-2xl border border-gray-200/60 flex items-center justify-between text-xs">
          <div>
            <span className="font-black text-gray-900 text-sm block">
              #{appointment.tokenNumber} {appointment.patientName}
            </span>
            <span className="text-[11px] text-gray-500 font-medium mt-0.5 block">
              📞 {appointment.patientPhone}
            </span>
          </div>

          <div className="text-right">
            <span className="text-[10px] font-extrabold text-gray-500 uppercase tracking-wider block">
              Current Slot
            </span>
            <span className="font-bold text-gray-800 text-xs flex items-center gap-1 mt-0.5 justify-end">
              <ClockIcon /> {appointment.appointmentDate ? String(appointment.appointmentDate).split("T")[0] : ""} @ {appointment.slotStartTime}
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* New Date Selector */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 block">
              New Appointment Date
            </label>
            <input
              type="date"
              value={newDate}
              min={getTodayString()}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 bg-white text-xs font-bold text-gray-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all cursor-pointer shadow-2xs"
              required
            />
          </div>

          {/* New Time Slot Custom Dropdown */}
          <div className="space-y-1.5 relative">
            <label className="text-xs font-bold text-gray-700 block">
              New Time Slot
            </label>
            <button
              type="button"
              onClick={() => setIsTimeDropdownOpen(!isTimeDropdownOpen)}
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 bg-white text-xs font-bold text-gray-900 flex items-center justify-between outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all cursor-pointer shadow-2xs hover:border-gray-300"
            >
              <span className="flex items-center gap-2">
                <ClockIcon />
                <span>
                  {newTime} {parseInt(newTime.split(":")[0], 10) >= 12 ? "PM" : "AM"}
                </span>
              </span>
              <ChevronDownIcon className={`transition-transform duration-200 ${isTimeDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {isTimeDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsTimeDropdownOpen(false)}
                />
                <div className="absolute left-0 right-0 bottom-full mb-1 z-50 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-48 overflow-y-auto p-1.5 space-y-0.5 animate-fadeIn">
                  {TIME_SLOTS.map((slot) => {
                    const formatted = `${slot} ${parseInt(slot.split(":")[0], 10) >= 12 ? "PM" : "AM"}`;
                    const isSelected = newTime === slot;
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => {
                          setNewTime(slot);
                          setIsTimeDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 rounded-xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
                          isSelected
                            ? "bg-primary-50 text-primary-900 font-extrabold"
                            : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <span>{formatted}</span>
                        {isSelected && <CheckIcon />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold transition-all cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-extrabold shadow-md shadow-primary-600/20 transition-all cursor-pointer hover:scale-[1.02] active:scale-95 disabled:opacity-50"
            >
              {isSubmitting ? "Rescheduling..." : "Confirm Reschedule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RescheduleModal;
